import { onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';

import AmplitudeEngagementService from '@/shared/services/analytics/AmplitudeEngagementService';

/**
 * Initialize and manage Amplitude Guides & Surveys SDK
 * @param {Object} options
 * @param {string} options.apiKey - Amplitude API key
 * @param {Object} options.segmentAnalytics - Segment analytics instance (window.analytics)
 * @param {Object} options.store - Vuex store instance
 * @param {Object} options.initOptions - Amplitude init options (serverZone, logLevel, locale, nonce)
 * @returns {AmplitudeEngagementService} Amplitude Engagement service instance
 */
export default function useAmplitudeEngagementInit({
  apiKey,
  segmentAnalytics,
  store,
  initOptions = {},
}) {
  const router = useRouter();

  // Create Amplitude Engagement service instance
  const amplitudeEngagement = new AmplitudeEngagementService(apiKey, segmentAnalytics);

  // Initialize SDK when component mounts
  onMounted(() => {
    if (apiKey) {
      amplitudeEngagement.init(initOptions);

      // Set up router integration for SPA navigation
      amplitudeEngagement.setRouter(async (url) => {
        await router.push(url);
      });
    }
  });

  // Watch for user profile changes and boot Amplitude Engagement
  if (store) {
    watch(
      () => store.state.profile,
      async (profile) => {
        if (profile && profile.id && profile.organization_id) {
          // Boot with user information
          await amplitudeEngagement.boot({
            user_id: String(profile.id),
            device_id: profile.device_id || undefined,
            user_properties: {
              name: profile.name,
              email: profile.email,
              organization_id: profile.organization_id,
              organization_name: profile.organization_name || window.organization_name,
              business_name: profile.business_name,
              is_anonymous: profile.is_anonymous,
              is_parent: profile.is_parent,
              role: profile.role,
            },
          });

          // Set user properties for variable substitution in guides/surveys
          amplitudeEngagement.setUserProperties({
            name: profile.name,
            email: profile.email,
            organization_id: profile.organization_id,
            organization_name: profile.organization_name || window.organization_name,
            business_name: profile.business_name,
          });
        }
      },
      { immediate: true },
    );
  }

  return amplitudeEngagement;
}
